// src/components/mitchRoom/revenueAnalytics/RevenueChart.js
import React, { useState, useMemo } from 'react';
import { Download } from 'lucide-react';
import { toast } from 'sonner';
import {
  ComposedChart,
  Line,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { Button } from 'components/ui/button';
import { Switch } from 'components/ui/switch';
import { Label } from 'components/ui/label';
import { Tabs, TabsList, TabsTrigger } from 'components/ui/tabs';
import { formatCurrency, formatPeriod, formatAxisCurrency } from 'utils/format';

const TYPE_SERIES = [
  { key: 'total', label: 'Total', color: '#1072CA', strokeWidth: 2 },
  { key: 'new_subscription', label: 'New Subscription', color: '#59A14F', strokeWidth: 1.5 },
  { key: 'resubscription', label: 'Resubscription', color: '#76B7B2', strokeWidth: 1.5 },
  { key: 'renewal', label: 'Renewal', color: '#F28E2B', strokeWidth: 1.5 },
  { key: 'one_time', label: 'One Time', color: '#B07AA1', strokeWidth: 1.5 },
];

// Who the buyer was, independent of the product lifecycle. Same colours as
// the Pacing tab so the two read as one system.
const BUYER_SERIES = [
  { key: 'bc_new', label: 'New customers', color: '#1baf7a' },
  { key: 'bc_winback', label: 'Win-back', color: '#2a78d6' },
  { key: 'bc_expansion', label: 'Expansion', color: '#eb6834' },
  { key: 'bc_renewal', label: 'Recurring', color: '#eda100' },
  { key: 'bc_unattributed', label: 'Unattributed', color: '#9e9e9e' },
];

// Stacked product split — fixed slot order, validated palette.
// Each has a `countKey` so the same series can be plotted as units sold
// rather than value (the £ / # toggle).
const PRODUCT_SERIES = [
  { key: 'product_monthly', countKey: 'count_monthly', label: 'Monthly', color: '#2a78d6' },
  { key: 'product_monthly_preseason', countKey: 'count_monthly_preseason', label: 'Monthly + Pre-Season', color: '#eb6834' },
  { key: 'product_preseason', countKey: 'count_preseason', label: 'Pre-Season Pack', color: '#1baf7a' },
  { key: 'product_annual', countKey: 'count_annual', label: 'Annual / Season Pass', color: '#eda100' },
  { key: 'product_other', countKey: 'count_other', label: 'Other', color: '#e87ba4' },
];

const COMPARISON_COLOR = '#9e9e9e';

export default function RevenueChart({ data, comparisonData, comparisonLabel }) {
  // 'buyer' = stacked bars by buyer class (default — the question the page
  // exists to answer), 'type' = lines by order type, 'product' = by product
  const [viewMode, setViewMode] = useState('buyer');

  // 'value' = £, 'count' = units sold. Only meaningful in product mode,
  // where every series has a count equivalent.
  const [measure, setMeasure] = useState('value');
  const showCounts = measure === 'count' && viewMode === 'product';

  const SERIES_BY_MODE = { type: TYPE_SERIES, product: PRODUCT_SERIES, buyer: BUYER_SERIES };
  const seriesConfig = SERIES_BY_MODE[viewMode];

  // In count mode the series read from countKey instead of key
  const dataKeyFor = (s) => (showCounts && s.countKey ? s.countKey : s.key);
  const fmtAxis = (v) =>
    showCounts ? Number(v ?? 0).toLocaleString('en-GB') : formatAxisCurrency(v);

  // Track which series are visible per mode (all on by default)
  const [visibleByMode, setVisibleByMode] = useState({
    type: TYPE_SERIES.reduce((acc, s) => ({ ...acc, [s.key]: true }), {}),
    product: PRODUCT_SERIES.reduce((acc, s) => ({ ...acc, [s.key]: true }), {}),
    buyer: BUYER_SERIES.reduce((acc, s) => ({ ...acc, [s.key]: true }), {}),
  });
  const visibleSeries = visibleByMode[viewMode];

  // Track cumulative mode
  const [isCumulative, setIsCumulative] = useState(false);

  // Merge in the comparison series and apply the cumulative transform.
  //
  // Alignment is by ORDINAL POSITION IN AN EQUAL-LENGTH SPINE, not by raw
  // array index. Both series are zero-filled server-side, so equal lengths
  // mean position i really is the same offset into each range; if the
  // lengths ever differ we align from the END (most recent), because that
  // is the edge the reader is looking at. The old raw-index merge silently
  // shifted the whole comparison line whenever one series had a gap.
  const chartData = useMemo(() => {
    if (!data || data.length === 0) return [];

    const allKeys = [...TYPE_SERIES, ...PRODUCT_SERIES, ...BUYER_SERIES]
      .flatMap((s) => [s.key, s.countKey])
      .filter(Boolean);
    const comp = Array.isArray(comparisonData) ? comparisonData : null;
    const offset = comp ? comp.length - data.length : 0;

    let points = data.map((point, i) => {
      const merged = { ...point };
      const c = comp ? comp[i + offset] : null;
      if (c) {
        merged.comparison_total = c.total || 0;
        merged.comparison_period = c.period;
      }
      return merged;
    });

    if (!isCumulative) return points;

    const running = {};
    return points.map((point) => {
      const out = { period: point.period, comparison_period: point.comparison_period };
      [...allKeys, 'comparison_total'].forEach((key) => {
        if (point[key] === undefined) return;
        running[key] = (running[key] || 0) + (point[key] || 0);
        out[key] = running[key];
      });
      return out;
    });
  }, [data, comparisonData, isCumulative]);

  // Click toggles a series; Shift+click isolates it (click again to restore all)
  const toggleSeries = (key, isolate = false) => {
    setVisibleByMode((prevAll) => {
      const prev = prevAll[viewMode];
      let next;
      if (isolate) {
        const isAlreadySolo = seriesConfig.every((s) =>
          s.key === key ? prev[s.key] : !prev[s.key]
        );
        next = {};
        seriesConfig.forEach((s) => {
          next[s.key] = isAlreadySolo ? true : s.key === key;
        });
      } else {
        next = { ...prev, [key]: !prev[key] };
      }
      return { ...prevAll, [viewMode]: next };
    });
  };

  const handleExportCsv = () => {
    const visible = seriesConfig.filter((s) => visibleSeries[s.key]);
    if (visible.length === 0 || chartData.length === 0) {
      toast.error('No visible series to export');
      return;
    }
    const hasComparison = comparisonData && comparisonData.length > 0;
    const header = [
      'period',
      ...visible.map((s) => s.label),
      ...(hasComparison ? [`${comparisonLabel || 'Comparison'} total`] : []),
    ];
    const rows = chartData.map((point) => [
      point.period,
      ...visible.map((s) => point[dataKeyFor(s)] ?? 0),
      ...(hasComparison ? [point.comparison_total ?? ''] : []),
    ]);
    const csv = [header, ...rows].map((row) => row.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `revenue-${viewMode}-${showCounts ? 'counts' : 'value'}-${isCumulative ? 'cumulative' : 'periodic'}.csv`;
    link.click();
    URL.revokeObjectURL(url);
    toast.success('Revenue CSV downloaded');
  };

  if (!data || data.length === 0) {
    return (
      <div className="rounded-token-large border border-borderGrey bg-white p-4">
        <h3 className="text-token-xl font-semibold">Revenue Over Time</h3>
        <div className="flex h-[300px] items-center justify-center">
          <p className="text-token-md text-muted-foreground">No data available</p>
        </div>
      </div>
    );
  }

  const stackedSeries = viewMode === 'product' ? PRODUCT_SERIES : BUYER_SERIES;
  const visibleStackKeys = stackedSeries.filter((s) => visibleSeries[s.key]).map((s) => s.key);
  const topStackKey = visibleStackKeys[visibleStackKeys.length - 1];
  const showComparison = comparisonData && comparisonData.length > 0;

  const tooltipFormatter = (value, name) => {
    if (name === 'comparison_total') {
      return [formatCurrency(value), comparisonLabel || 'Comparison'];
    }
    const all = [...TYPE_SERIES, ...PRODUCT_SERIES, ...BUYER_SERIES];
    const s = all.find((x) => x.key === name || x.countKey === name);
    const isCount = s && s.countKey === name;
    return [isCount ? `${Number(value ?? 0).toLocaleString('en-GB')} sold` : formatCurrency(value), s?.label || name];
  };

  return (
    <div className="rounded-token-large border border-borderGrey bg-white p-4">
      {/* Header with title and controls */}
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-token-xl font-semibold">
          {showCounts ? 'Units Sold Over Time' : 'Revenue Over Time'}
        </h3>
        <div className="flex items-center gap-4">
          <Tabs value={viewMode} onValueChange={setViewMode}>
            <TabsList>
              <TabsTrigger value="buyer">By buyer</TabsTrigger>
              <TabsTrigger value="product">By product</TabsTrigger>
              <TabsTrigger value="type">By type</TabsTrigger>
            </TabsList>
          </Tabs>
          {viewMode === 'product' && (
            <Tabs value={measure} onValueChange={setMeasure}>
              <TabsList>
                <TabsTrigger value="value">£ value</TabsTrigger>
                <TabsTrigger value="count"># sold</TabsTrigger>
              </TabsList>
            </Tabs>
          )}
          <div className="flex items-center gap-2">
            <Switch
              id="revenue-cumulative"
              checked={isCumulative}
              onCheckedChange={setIsCumulative}
            />
            <Label htmlFor="revenue-cumulative" className="cursor-pointer text-token-base font-normal">
              Cumulative
            </Label>
          </div>
          <Button variant="outline" size="sm" onClick={handleExportCsv}>
            <Download />
            CSV
          </Button>
        </div>
      </div>

      {/* Series toggles — click to toggle, Shift+click to isolate */}
      <div className="mb-3 flex flex-wrap gap-1.5">
        {seriesConfig.map((series) => {
          const on = visibleSeries[series.key];
          return (
            <button
              key={series.key}
              type="button"
              onClick={(e) => toggleSeries(series.key, e.shiftKey)}
              title="Click to toggle · Shift+click to isolate"
              aria-pressed={on}
              className="rounded-token-large border px-2.5 py-0.5 text-xs font-medium transition-colors"
              style={
                on
                  ? { backgroundColor: series.color, borderColor: series.color, color: '#fff' }
                  : { backgroundColor: 'transparent', borderColor: series.color, color: series.color }
              }
            >
              {series.label}
            </button>
          );
        })}
        {showComparison && (
          <span
            className="rounded-token-large border border-dashed px-2.5 py-0.5 text-xs font-medium"
            style={{ borderColor: COMPARISON_COLOR, color: COMPARISON_COLOR }}
          >
            {comparisonLabel || 'Comparison'} (total)
          </span>
        )}
      </div>

      {/* Chart */}
      <div className="h-[350px] w-full">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={chartData} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#DADADA" vertical={false} />
            <XAxis dataKey="period" tick={{ fontSize: 11 }} tickFormatter={formatPeriod} />
            <YAxis tickFormatter={fmtAxis} tick={{ fontSize: 11 }} width={55} />
            <Tooltip
              formatter={tooltipFormatter}
              labelFormatter={(label, payload) => {
                const compPeriod = payload?.[0]?.payload?.comparison_period;
                const base = formatPeriod(label);
                return compPeriod ? `${base} (vs ${formatPeriod(compPeriod)})` : base;
              }}
            />
            {viewMode === 'type' &&
              TYPE_SERIES.map(
                (series) =>
                  visibleSeries[series.key] && (
                    <Line
                      key={series.key}
                      type="monotone"
                      dataKey={series.key}
                      stroke={series.color}
                      strokeWidth={series.strokeWidth}
                      dot={false}
                      activeDot={{ r: 5 }}
                      isAnimationActive={false}
                    />
                  )
              )}
            {viewMode !== 'type' &&
              stackedSeries.map(
                (series) =>
                  visibleSeries[series.key] && (
                    <Bar
                      key={series.key}
                      dataKey={dataKeyFor(series)}
                      stackId="stack"
                      fill={series.color}
                      stroke="#fff"
                      strokeWidth={1}
                      radius={series.key === topStackKey ? [3, 3, 0, 0] : 0}
                      isAnimationActive={false}
                    />
                  )
              )}
            {showComparison && !showCounts && (
              <Line
                type="monotone"
                dataKey="comparison_total"
                stroke={COMPARISON_COLOR}
                strokeWidth={2}
                strokeDasharray="6 4"
                dot={false}
                activeDot={{ r: 4 }}
                isAnimationActive={false}
              />
            )}
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
